import { createTheme, MantineTheme, CSSVariablesResolver } from '@mantine/core';
import { GLOBAL_STYLES, theme as partialTheme } from './themeVariables';

export const themedResolver: CSSVariablesResolver = (theme) => ({
  variables: {},
  light: {
    '--mantine-color-text': theme.black,
    '--mantine-color-body': theme.white,
    '--mantine-color-dimmed': theme.colors.dark[2],
    '--mantine-color-placeholder': theme.colors.dark[2],
    '--mantine-color-paper': theme.white,
  },
  dark: {},
});

export const theme = createTheme({
  ...partialTheme,
  components: {
    Button: {
      defaultProps: {
        size: 'md',
        radius: GLOBAL_STYLES.borderRadius,
      },
      styles: (theme: MantineTheme) => ({
        root: {
          '--button-radius': `var(--mantine-radius-${GLOBAL_STYLES.borderRadius})`,
          '--button-hover-radius': `var(--mantine-radius-${GLOBAL_STYLES.borderRadius})`,
          borderRadius: `var(--mantine-radius-${GLOBAL_STYLES.borderRadius})`,
          '&:active': {
            transform: 'translateY(1px)',
          },
          '&[data-variant="primary"]': {
            backgroundColor: theme.colors.brand[5],
            color: theme.white,
            '&:hover': {
              backgroundColor: theme.colors.brand[6],
            },
          },
          '&[data-variant="secondary"]': {
            backgroundColor: theme.colors.secondary[5],
            color: theme.colors.brand[9],
            '&:hover': {
              backgroundColor: theme.colors.secondary[6],
            },
          },
          '&[data-variant="accent"]': {
            backgroundColor: theme.colors.accent[5],
            color: theme.white,
            '&:hover': {
              backgroundColor: theme.colors.accent[6],
            },
          },
          '&[data-variant="danger"]': {
            backgroundColor: theme.colors.danger[5],
            color: theme.white,
            '&:hover': {
              backgroundColor: theme.colors.danger[6],
            },
          },
          '&[data-variant="outline"]': {
            backgroundColor: 'transparent',
            borderColor: theme.colors.brand[5],
            color: theme.colors.brand[5],
            '&:hover': {
              backgroundColor: theme.colors.brand[0],
            },
          },
        },
      }),
    },
    Checkbox: {
      styles: {
        input: {
          width: '1.25rem',
          height: '1.25rem',
        },
      },
    },
    Anchor: {
      defaultProps: {
        underline: 'hover',
      },
      styles: (theme: MantineTheme) => ({
        root: {
          color: theme.colors.secondary[6],
          '&:hover': {
            color: theme.colors.secondary[8],
            textDecoration: 'underline',
          },
        },
      }),
    },
    Badge: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    Image: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    Skeleton: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    Avatar: {
      defaultProps: {
        radius: GLOBAL_STYLES.avatarRadius,
      },
    },
    Chip: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    Card: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    Paper: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    Alert: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    Input: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    Select: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
      styles: (theme: MantineTheme) => ({
        input: {
          '--input-radius': `var(--mantine-radius-${GLOBAL_STYLES.borderRadius})`,
        },
        dropdown: {
          borderRadius: `var(--mantine-radius-${GLOBAL_STYLES.borderRadius})`,
        },
        option: {
          borderRadius: `var(--mantine-radius-${GLOBAL_STYLES.borderRadius})`,
          '&[data-selected]': {
            '&[data-status="published"]': {
              backgroundColor: theme.colors.brand[0],
              color: theme.colors.brand[7],
            },
            '&[data-status="draft"]': {
              backgroundColor: theme.colors.danger[0],
              color: theme.colors.danger[7],
            },
          },
        },
      }),
    },
    TextInput: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    Textarea: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    Modal: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    Popover: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    NumberInput: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    PasswordInput: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    MultiSelect: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    ColorInput: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    FileInput: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
    },
    ActionIcon: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
      styles: () => ({
        root: {
          '--ai-radius': `var(--mantine-radius-${GLOBAL_STYLES.borderRadius})`,
          '--ai-hover-radius': `var(--mantine-radius-${GLOBAL_STYLES.borderRadius})`,
          borderRadius: `var(--mantine-radius-${GLOBAL_STYLES.borderRadius})`,
        },
      }),
    },
    Menu: {
      defaultProps: {
        radius: GLOBAL_STYLES.borderRadius,
      },
      styles: (theme: MantineTheme) => ({
        dropdown: {
          borderRadius: `var(--mantine-radius-${GLOBAL_STYLES.borderRadius})`,
        },
        item: {
          borderRadius: `var(--mantine-radius-${GLOBAL_STYLES.borderRadius})`,
          '&[data-danger]': {
            color: theme.colors.danger[5],
            '&:hover': {
              backgroundColor: theme.colors.danger[0],
              color: theme.colors.danger[7],
            },
          },
        },
      }),
    },
  },
});
